import React, { useMemo, useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, RefreshCw, Mail, Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { PageLayout, PageHeader, PageCard, pageStyles } from '../PageLayout';

const VERLAENGERUNG_BACKEND_URL = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_VERLAENGERUNG_BACKEND_URL) || '';
const VERLAENGERUNG_API_SECRET = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_VERLAENGERUNG_API_SECRET) || '';
const VERLAENGERUNG_AKTIV = Boolean(VERLAENGERUNG_BACKEND_URL && VERLAENGERUNG_API_SECRET);

const checkMatch = (allowed: any, c: any) => {
    const normalizeNumber = (v: any) => v ? String(v).replace(/[^0-9]/g, '').replace(/^0+/, '') : '';
    const normalizeName = (n: any) => n ? n.replace(/[:.;,]/g, ' ').replace(/\s+/g, ' ').trim() : '';

    if (!allowed || !Array.isArray(allowed)) return false;
    if (allowed.some(u => u.role === 'admin')) return true;
    return allowed.some(u => {
        if (!u) return false;
        const uAdv = normalizeNumber(u.adv), cAdp = normalizeNumber(c.adp), cBtnr = normalizeNumber(c.btnr), uBtnr = normalizeNumber(u.btnr);
        if (uAdv.length > 2 && ((cAdp && uAdv.includes(cAdp)) || (cBtnr && uAdv.includes(cBtnr)))) return true;
        if (uBtnr.length > 2 && ((cAdp && uBtnr.includes(cAdp)) || (cBtnr && uBtnr.includes(cBtnr)))) return true;
        const uName = normalizeName(u.name).toLowerCase(), cAdpName = normalizeName(c.adp).toLowerCase();
        return uName.length > 2 && cAdpName.includes(uName);
    });
};

const parseAnyDate = (s: any) => {
    if (!s) return null;
    if (typeof s === 'string' && s.includes('-')) {
        const [y, m, d] = s.split('-');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    }
    if (typeof s === 'string' && s.includes('.')) {
        const [d, m, y] = s.split('.');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    }
    return null;
};

const hasEmail = (c: any) => ((c?.mail || c?.email || '').trim() !== '');

const VerlaengerungView = React.memo(({ customers, currentUser, allUsers, contractStates, onToggleContractState, onSelectCustomer, showToast }: any) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [expTab, setExpTab] = useState<'open' | 'done'>('open');
    const [expDateFrom, setExpDateFrom] = useState('');
    const [expDateTo, setExpDateTo] = useState('');
    const [expSparteFilter, setExpSparteFilter] = useState('');
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [sending, setSending] = useState(false);
    const [bestaetigungen, setBestaetigungen] = useState<any[]>([]);
    const [bestaetigungenLoading, setBestaetigungenLoading] = useState(false);

    const myCustomers = useMemo(() => {
        if (!currentUser) return [];
        const cust = Array.isArray(customers) ? customers : [];
        const users = Array.isArray(allUsers) ? allUsers : [];
        if (currentUser.role === 'admin') return cust;
        const allowedUserIds = [currentUser.id, ...(currentUser.allowedToView || [])];
        return cust.filter((c: any) => checkMatch(users.filter((u: any) => allowedUserIds.includes(u.id)), c));
    }, [customers, currentUser, allUsers]);

    const getFilteredExpirations = (filterTab: 'open' | 'done' | null) => {
        let results: any = [];
        if (!currentUser || !Array.isArray(myCustomers)) return [];

        myCustomers.forEach((c: any) => {
            if (!hasEmail(c)) return;
            (c.verträge || []).forEach((v: any) => {
                const safeId = (v.vsnr || '').replace(/\//g, '_');
                const isDone = (contractStates && contractStates[safeId]) === 'done';
                const matchesTab = filterTab === null || (filterTab === 'open' && !isDone) || (filterTab === 'done' && isDone);

                if (expSparteFilter && v.sparte !== expSparteFilter) return;

                let matchesDateFilter = true;
                const expiryDate = parseAnyDate(v.ablauf);
                if (!expiryDate) matchesDateFilter = false;
                else if (expDateFrom || expDateTo) {
                    const from = expDateFrom ? new Date(expDateFrom) : new Date(0);
                    const to = expDateTo ? new Date(expDateTo) : new Date(9999, 11, 31);
                    matchesDateFilter = expiryDate >= from && expiryDate <= to;
                } else {
                    const defaultFrom = new Date(2000, 0, 1);
                    const defaultTo = new Date();
                    defaultTo.setMonth(defaultTo.getMonth() + 5);
                    matchesDateFilter = expiryDate >= defaultFrom && expiryDate <= defaultTo;
                }

                const sparteLower = (v.sparte || '').toLowerCase().trim();
                const isKfzOrLeben = /kfz|kraftfahrzeug|lebensversicherung|^leben$/.test(sparteLower) || sparteLower === 'lv';
                if (matchesTab && matchesDateFilter && !isKfzOrLeben) {
                    results.push({ customer: c, contract: v, id: safeId, status: isDone ? 'done' : 'open' });
                }
            });
        });

        return results.sort((a: any, b: any) => (parseAnyDate(a.contract?.ablauf)?.getTime() || 0) - (parseAnyDate(b.contract?.ablauf)?.getTime() || 0));
    };

    const openCount = useMemo(() => getFilteredExpirations('open').length, [myCustomers, contractStates, expDateFrom, expDateTo, expSparteFilter, currentUser]);
    const doneCount = useMemo(() => getFilteredExpirations('done').length, [myCustomers, contractStates, expDateFrom, expDateTo, expSparteFilter, currentUser]);
    const displayedExpirations = useMemo(() => getFilteredExpirations(expTab), [myCustomers, contractStates, expTab, expDateFrom, expDateTo, expSparteFilter, currentUser]);

    const getItemKey = (item: any) => `${item.customer?.id || item.customer?.name}-${item.id}`;

    useEffect(() => {
        if (!VERLAENGERUNG_AKTIV) return;
        setBestaetigungenLoading(true);
        const url = new URL(`${VERLAENGERUNG_BACKEND_URL}/api/bestaetigungen`);
        url.searchParams.set('apiSecret', VERLAENGERUNG_API_SECRET);
        if (currentUser?.role !== 'admin' && currentUser?.name) url.searchParams.set('zustMA', currentUser.name);
        fetch(url.toString())
            .then((r) => r.json())
            .then((data) => setBestaetigungen(data.items || []))
            .catch(() => setBestaetigungen([]))
            .finally(() => setBestaetigungenLoading(false));
    }, [VERLAENGERUNG_AKTIV, currentUser?.role, currentUser?.name]);

    const sendVerlaengerungEmails = async () => {
        if (!VERLAENGERUNG_AKTIV || selectedKeys.size === 0) return;
        const items = displayedExpirations
            .filter((item: any) => selectedKeys.has(getItemKey(item)))
            .map((item: any) => {
                const zustMAName = item.customer?.zustMA || '';
                const adpUser = Array.isArray(allUsers) ? allUsers.find((u: any) => (u.name || '').trim() === (zustMAName || '').trim()) : null;
                const zustMaEmail = (adpUser?.email || currentUser?.email || '').trim();
                const beitrag = item.contract?.beitrag ?? item.contract?.JAB ?? item.contract?.jab ?? '';
                return {
                    customerName: [item.customer?.vorname, item.customer?.name].filter(Boolean).join(' ') || 'Kunde',
                    customerEmail: (item.customer?.mail || item.customer?.email || '').trim(),
                    customer_email: (item.customer?.mail || item.customer?.email || '').trim(),
                    vsnr: item.contract?.vsnr || '-',
                    sparte: item.contract?.sparte || '-',
                    ablauf: item.contract?.ablauf || '-',
                    gesellschaft: item.contract?.gesellschaft || '',
                    zustMA: zustMAName,
                    zust_ma: zustMAName,
                    zust_ma_email: zustMaEmail,
                    zustMaEmail: zustMaEmail,
                    beitrag: beitrag != null ? String(beitrag) : '',
                };
            })
            .filter((it: any) => it.customerEmail);
        if (items.length === 0) {
            showToast?.('Keine E-Mail-Adressen bei den ausgewählten Kunden.', 'error');
            return;
        }
        setSending(true);
        try {
            const res = await fetch(`${VERLAENGERUNG_BACKEND_URL}/api/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ apiSecret: VERLAENGERUNG_API_SECRET, items }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showToast?.(data.error || 'Versand fehlgeschlagen.', 'error');
                return;
            }
            showToast?.(`${data.sent ?? 0} E-Mail(s) gesendet.`);
            setSelectedKeys(new Set());
            setBestaetigungenLoading(true);
            const u = new URL(`${VERLAENGERUNG_BACKEND_URL}/api/bestaetigungen`);
            u.searchParams.set('apiSecret', VERLAENGERUNG_API_SECRET);
            if (currentUser?.role !== 'admin' && currentUser?.name) u.searchParams.set('zustMA', currentUser.name);
            fetch(u.toString()).then((r) => r.json()).then((d) => setBestaetigungen(d.items || [])).finally(() => setBestaetigungenLoading(false));
        } catch (e) {
            showToast?.('Netzwerkfehler beim E-Mail-Versand.', 'error');
        } finally {
            setSending(false);
        }
    };

    const sendSingleVerlaengerungEmail = async (item: any) => {
        if (!VERLAENGERUNG_AKTIV) return;
        const email = (item.customer?.mail || item.customer?.email || '').trim();
        if (!email) {
            showToast?.('Keine E-Mail-Adresse bei diesem Kunden.', 'error');
            return;
        }
        const zustMAName = item.customer?.zustMA || '';
        const adpUser = Array.isArray(allUsers) ? allUsers.find((u: any) => (u.name || '').trim() === (zustMAName || '').trim()) : null;
        const zustMaEmail = (adpUser?.email || currentUser?.email || '').trim();
        const beitrag = item.contract?.beitrag ?? item.contract?.JAB ?? item.contract?.jab ?? '';
        const payload = [{
            customerName: [item.customer?.vorname, item.customer?.name].filter(Boolean).join(' ') || 'Kunde',
            customerEmail: email,
            customer_email: email,
            vsnr: item.contract?.vsnr || '-',
            sparte: item.contract?.sparte || '-',
            ablauf: item.contract?.ablauf || '-',
            gesellschaft: item.contract?.gesellschaft || '',
            zustMA: zustMAName,
            zust_ma: zustMAName,
            zust_ma_email: zustMaEmail,
            zustMaEmail: zustMaEmail,
            beitrag: beitrag != null ? String(beitrag) : '',
        }];
        setSending(true);
        try {
            const res = await fetch(`${VERLAENGERUNG_BACKEND_URL}/api/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ apiSecret: VERLAENGERUNG_API_SECRET, items: payload }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showToast?.(data.error || 'Versand fehlgeschlagen.', 'error');
                return;
            }
            showToast?.('E-Mail mit Verlängerungs-Link wurde gesendet.');
            setBestaetigungenLoading(true);
            const u = new URL(`${VERLAENGERUNG_BACKEND_URL}/api/bestaetigungen`);
            u.searchParams.set('apiSecret', VERLAENGERUNG_API_SECRET);
            if (currentUser?.role !== 'admin' && currentUser?.name) u.searchParams.set('zustMA', currentUser.name);
            fetch(u.toString()).then((r) => r.json()).then((d) => setBestaetigungen(d.items || [])).finally(() => setBestaetigungenLoading(false));
        } catch (e) {
            showToast?.('Netzwerkfehler beim E-Mail-Versand.', 'error');
        } finally {
            setSending(false);
        }
    };

    const getDaysUntil = (dateStr: string) => {
        const date = parseAnyDate(dateStr);
        if (!date) return 999;
        const today = new Date();
        return Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    };

    const getUrgencyColor = (daysUntil: number) => {
        if (daysUntil <= 7) return { bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-100' };
        if (daysUntil <= 30) return { bg: 'bg-orange-50', text: 'text-orange-700', badge: 'bg-orange-100' };
        return { bg: 'bg-yellow-50', text: 'text-yellow-700', badge: 'bg-yellow-100' };
    };

    const spartenOptions = useMemo(() => {
        const raw = Array.from(new Set((myCustomers || []).flatMap((c: any) => (c.verträge || []).map((v: any) => v.sparte))));
        return raw.filter((s: any) => s != null && s !== '' && !String(s).toLowerCase().match(/leben|kfz/)).sort();
    }, [myCustomers]);

    return (
        <PageLayout>
            <PageHeader
                title="Verlängerung"
                subtitle={
                    <span className="flex items-center gap-2 mt-1">
                        <Mail size={16} />
                        Nur Kunden mit E-Mail – Einzel- oder Sammelversand, Bestätigungen sichtbar
                    </span>
                }
            />

            {VERLAENGERUNG_AKTIV && (
                <>
                    <p className={`mb-4 text-sm ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                        <a href={`${VERLAENGERUNG_BACKEND_URL}/kunde/preview`} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">Vorschau der Kundenseite</a> (wie der Kunde den Link sieht)
                    </p>
                <PageCard className="mb-6">
                    <h3 className={`text-lg font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        <CheckCircle size={20} className="text-green-500" />
                        Wer hat die Tarifanpassung angenommen? ({bestaetigungen.length})
                    </h3>
                    {bestaetigungenLoading ? (
                        <p className={isDark ? 'text-white/60' : 'text-slate-600'}>Lade …</p>
                    ) : bestaetigungen.length === 0 ? (
                        <p className={isDark ? 'text-white/60' : 'text-slate-600'}>Bisher keine Bestätigungen. Nach dem Versand der E-Mails erscheinen sie hier, sobald der Kunde den Link bestätigt hat.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className={isDark ? 'border-b border-white/20 text-white/80' : 'border-b border-slate-200 text-slate-600'}>
                                        <th className="text-left py-2 pr-4">Kunde</th>
                                        <th className="text-left py-2 pr-4">Sparte / VSNR</th>
                                        <th className="text-left py-2 pr-4">Zust. MA</th>
                                        <th className="text-left py-2">Bestätigt am</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bestaetigungen.map((b: any) => (
                                        <tr key={b.id} className={isDark ? 'border-b border-white/10' : 'border-b border-slate-100'}>
                                            <td className="py-2 pr-4 font-medium">{b.customer_name}</td>
                                            <td className="py-2 pr-4">{b.sparte} / {b.vsnr}</td>
                                            <td className="py-2 pr-4">{b.zust_ma || '–'}</td>
                                            <td className="py-2">{b.confirmed_at ? new Date(b.confirmed_at).toLocaleString('de-DE') : '–'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </PageCard>
                </>
            )}

            <PageCard className="mt-6 p-6 space-y-4">
                <div>
                    <h3 className={`text-lg font-bold mb-1 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        <Mail size={20} className="text-cyan-500" />
                        E-Mail mit Vertragsinformationen senden
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                        Wählen Sie Verträge aus der Liste (Tab „Offen“) und senden Sie eine E-Mail. Der Kunde erhält einen Link zu den Vertragsinformationen und kann die Tarifanpassung annehmen oder Ihnen eine Nachricht schicken.
                    </p>
                </div>

                <div className="flex gap-3 flex-wrap">
                    <button
                        onClick={() => setExpTab('open')}
                        className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all border ${
                            expTab === 'open'
                                ? (isDark ? 'bg-red-500/50 border-red-400/80 text-white shadow-lg shadow-red-500/20' : 'bg-red-500 border-red-600 text-white shadow-lg shadow-red-500/20')
                                : (isDark ? 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/40' : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100')
                        }`}
                    >
                        📋 Offen ({openCount})
                    </button>
                    <button
                        onClick={() => setExpTab('done')}
                        className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all border ${
                            expTab === 'done'
                                ? (isDark ? 'bg-green-500/50 border-green-400/80 text-white shadow-lg shadow-green-500/20' : 'bg-green-500 border-green-600 text-white shadow-lg shadow-green-500/20')
                                : (isDark ? 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/40' : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100')
                        }`}
                    >
                        ✓ Erledigt ({doneCount})
                    </button>
                </div>

                <div className={`border-t ${isDark ? 'border-white/20' : 'border-slate-200'} pt-4 space-y-3`}>
                    <p className={`text-xs font-bold ${isDark ? 'text-white/80' : 'text-slate-700'} uppercase`}>Filter</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="space-y-1">
                            <label className={`text-xs font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'} block`}>Von Datum:</label>
                            <input
                                type="date"
                                value={expDateFrom}
                                onChange={(e) => setExpDateFrom(e.target.value)}
                                className={`w-full px-3 py-2 rounded-xl text-sm transition-all ${isDark ? 'glass-input' : 'bg-slate-50 border border-slate-200 text-slate-900 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20'}`}
                            />
                        </div>
                        <div className="space-y-1">
                            <label className={`text-xs font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'} block`}>Bis Datum:</label>
                            <input
                                type="date"
                                value={expDateTo}
                                onChange={(e) => setExpDateTo(e.target.value)}
                                className={`w-full px-3 py-2 rounded-xl text-sm transition-all ${isDark ? 'glass-input' : 'bg-slate-50 border border-slate-200 text-slate-900 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20'}`}
                            />
                        </div>
                        <div className="space-y-1">
                            <label className={`text-xs font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'} block`}>Sparte:</label>
                            <select
                                value={expSparteFilter}
                                onChange={(e) => setExpSparteFilter(e.target.value)}
                                className={`w-full px-3 py-2 rounded-xl text-sm transition-all ${isDark ? 'glass-input bg-white/5' : 'bg-slate-50 border border-slate-200 text-slate-900 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20'}`}
                            >
                                <option value="">— Alle Sparten —</option>
                                {spartenOptions.map((sparte: any) => (
                                    <option key={sparte} value={sparte}>{sparte}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <button
                        onClick={() => { setExpDateFrom(''); setExpDateTo(''); setExpSparteFilter(''); }}
                        className={`text-xs font-bold transition px-3 py-1.5 rounded-full ${isDark ? 'text-white/60 hover:text-white/80 hover:bg-white/10' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
                    >
                        ↻ Alle Filter zurücksetzen
                    </button>
                </div>

                {VERLAENGERUNG_AKTIV && expTab === 'open' && displayedExpirations.length > 0 && (
                    <div className={`flex flex-wrap items-center gap-4 py-3 px-4 rounded-xl border ${isDark ? 'bg-cyan-500/10 border-cyan-400/30' : 'bg-cyan-50 border-cyan-200'}`}>
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={selectedKeys.size === displayedExpirations.length && displayedExpirations.length > 0}
                                onChange={(e) => setSelectedKeys(e.target.checked ? new Set(displayedExpirations.map((i: any) => getItemKey(i))) : new Set())}
                                className="rounded h-4 w-4"
                            />
                            <span className={`text-sm font-semibold ${isDark ? 'text-cyan-200' : 'text-cyan-800'}`}>Alle auswählen</span>
                        </label>
                        {selectedKeys.size > 0 && (
                            <>
                                <span className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-slate-700'}`}>{selectedKeys.size} ausgewählt</span>
                                <button
                                    onClick={sendVerlaengerungEmails}
                                    disabled={sending}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-50 shadow-md"
                                >
                                    {sending ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
                                    Alle ausgewählten E-Mails senden
                                </button>
                            </>
                        )}
                    </div>
                )}

                {!VERLAENGERUNG_AKTIV && (
                    <p className={`text-sm py-3 px-4 rounded-xl border ${isDark ? 'border-amber-400/40 bg-amber-500/10 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                        Verlängerungs-Backend nicht konfiguriert: In der Projekt-<code className="font-mono text-xs">.env</code> <code>VITE_VERLAENGERUNG_BACKEND_URL</code> und <code>VITE_VERLAENGERUNG_API_SECRET</code> setzen, dann App neu bauen.
                    </p>
                )}

                {displayedExpirations.length === 0 ? (
                    <div className={pageStyles.empty(isDark)}>
                        <Mail size={48} className={`mx-auto mb-3 ${isDark ? 'text-white/30' : 'text-slate-300'}`} />
                        <p className="font-medium">Keine Verträge mit E-Mail in dieser Ansicht</p>
                        <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Nur Kunden mit hinterlegter E-Mail erscheinen hier. Filter anpassen oder Tab „Erledigt“ prüfen.</p>
                    </div>
                ) : (
                    <div className={`border-t ${isDark ? 'border-white/20' : 'border-slate-200'} pt-4`}>
                        <p className={`text-xs font-bold uppercase mb-3 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Verträge – Klicken zum Auswählen / E-Mail senden</p>
                        <div className={`divide-y ${isDark ? 'divide-white/10' : 'divide-slate-100'}`}>
                        {displayedExpirations.map((item: any, idx: any) => {
                            const daysUntil = getDaysUntil(item.contract?.ablauf);
                            const urgency = getUrgencyColor(daysUntil);
                            const isOverdue = daysUntil < 0;
                            const isSelected = selectedKeys.has(getItemKey(item));
                            return (
                                <div
                                    key={idx}
                                    className={`p-4 transition-all border-l-4 ${
                                        isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
                                    } ${
                                        item.status === 'done' ? '' : (
                                            isOverdue ? (isDark ? 'border-l-red-600 bg-red-500/10' : 'border-l-red-600 bg-red-50') :
                                            daysUntil <= 7 ? 'border-l-red-500' :
                                            daysUntil <= 30 ? 'border-l-orange-500' :
                                            'border-l-yellow-500'
                                        )
                                    } ${isSelected ? (isDark ? 'ring-2 ring-cyan-400/50' : 'ring-2 ring-cyan-400') : ''}`}
                                    onClick={() => onSelectCustomer?.(item.customer)}
                                >
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                        {expTab === 'open' && (
                                            <div className="flex items-center gap-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                                {VERLAENGERUNG_AKTIV ? (
                                                    <>
                                                        <input
                                                            type="checkbox"
                                                            id={`verl-${idx}`}
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                const key = getItemKey(item);
                                                                setSelectedKeys((prev) => {
                                                                    const next = new Set(prev);
                                                                    if (e.target.checked) next.add(key); else next.delete(key);
                                                                    return next;
                                                                });
                                                            }}
                                                            className="rounded h-4 w-4 cursor-pointer"
                                                        />
                                                        <label htmlFor={`verl-${idx}`} className={`text-sm font-medium cursor-pointer select-none ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Zum Versand auswählen</label>
                                                    </>
                                                ) : (
                                                    <span className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Backend nötig</span>
                                                )}
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                <p className={`font-bold text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.customer?.vorname} {item.customer?.name}</p>
                                                <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${isDark ? 'text-white/80 bg-white/10 border-white/20' : 'text-slate-700 bg-slate-100 border-slate-200'}`}>
                                                    {item.customer?.zustMA}
                                                </span>
                                            </div>
                                            <div className="flex gap-3 flex-wrap items-center text-sm">
                                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${item.status === 'done' ? (isDark ? 'bg-white/10 text-white/60' : 'bg-slate-100 text-slate-500') : (urgency.badge + ' ' + urgency.text)}`}>
                                                    {item.contract?.sparte}
                                                </span>
                                                <span className={`font-mono text-xs ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{item.contract?.vsnr}</span>
                                                <span className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>{item.customer?.mail || item.customer?.email}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                                            {VERLAENGERUNG_AKTIV && expTab === 'open' && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); sendSingleVerlaengerungEmail(item); }}
                                                    disabled={sending}
                                                    className="h-10 px-3 rounded-xl flex items-center gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold disabled:opacity-50"
                                                    title="Nur an diesen Kunden senden"
                                                >
                                                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                                                    E-Mail senden
                                                </button>
                                            )}
                                            <div className="text-right">
                                                <p className={`font-mono font-bold text-sm ${item.status === 'done' ? (isDark ? 'text-white/60' : 'text-slate-500') : urgency.text}`}>
                                                    {item.contract?.ablauf}
                                                </p>
                                                <p className={`text-xs font-semibold ${item.status === 'done' ? (isDark ? 'text-white/60' : 'text-slate-500') : urgency.text}`}>
                                                    {item.status === 'open' && (isOverdue ? `⚠️ Überfällig ${Math.abs(daysUntil)}d` : `${daysUntil} Tage`)}
                                                </p>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const nextStatus = item.status === 'done' ? 'open' : 'done';
                                                    onToggleContractState(item.id, nextStatus);
                                                }}
                                                className={`h-10 w-10 rounded-full flex items-center justify-center transition-all flex-shrink-0 font-bold border ${
                                                    expTab === 'open'
                                                        ? (isDark ? 'bg-white/10 border-white/20 text-white/60 hover:bg-green-500/50 hover:border-green-400/80 hover:text-white' : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-green-500 hover:border-green-600 hover:text-white')
                                                        : (isDark ? 'bg-green-500/50 border-green-400/80 text-white hover:bg-white/10 hover:border-white/20 hover:text-white/60' : 'bg-green-500 border-green-600 text-white hover:bg-slate-100 hover:border-slate-200 hover:text-slate-600')
                                                }`}
                                                title={expTab === 'open' ? 'Als erledigt markieren' : 'Zurück auf offen'}
                                            >
                                                {expTab === 'open' ? <CheckCircle size={18} /> : <RefreshCw size={18} />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        </div>
                    </div>
                )}
            </PageCard>

        </PageLayout>
    );
});

VerlaengerungView.displayName = 'VerlaengerungView';
export default VerlaengerungView;
